import { useEffect, useMemo, useState } from 'react';
import { Container } from '@mui/material';
import { motion } from 'framer-motion';
import style from './ProfetenPage.module.css';

type FixtureStatus = 'NS' | 'TBD' | '1H' | 'HT' | '2H' | 'ET' | 'P' | 'FT' | 'AET' | 'PEN' | 'BT' | 'SUSP' | 'INT' | 'PST' | 'CANC' | 'ABD' | 'AWD' | 'WO';

interface TeamRef { name: string; logo: string }
interface Fixture {
    id: number;
    kickoff: string;
    venue: string;
    status: FixtureStatus | string;
    league: string;
    stage: string;
    home: TeamRef;
    away: TeamRef;
    score: { home: number | null; away: number | null };
}

interface FixtureResponse {
    sport?: string;
    season: number;
    fixtures_upcoming: Fixture[];
    fixtures_finished: Fixture[];
}

type Sport = 'football' | 'handball';

const SPORTS: { key: Sport; label: string }[] = [
    { key: 'football', label: 'Fotball' },
    { key: 'handball', label: 'Håndball' },
];

interface Prediction {
    outcome: 'H' | 'U' | 'B';
    home: number | null;
    away: number | null;
}

const STORAGE_KEY = 'brynildProfeten.predictions.v1';

function readPredictions(): Record<string, Prediction> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

function writePredictions(p: Record<string, Prediction>) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } catch {
        /* ignore quota / disabled */
    }
}

function actualOutcome(f: Fixture): 'H' | 'U' | 'B' | null {
    if (f.score.home == null || f.score.away == null) return null;
    if (f.score.home > f.score.away) return 'H';
    if (f.score.home < f.score.away) return 'B';
    return 'U';
}

function pointsFor(f: Fixture, p: Prediction | undefined): { pts: number; perfect: boolean } | null {
    if (f.status !== 'FT' && f.status !== 'AET' && f.status !== 'PEN') return null;
    if (!p) return { pts: 0, perfect: false };
    const actual = actualOutcome(f);
    if (!actual) return { pts: 0, perfect: false };
    if (p.outcome !== actual) return { pts: 0, perfect: false };
    // Correct outcome → 2 points
    if (p.home != null && p.away != null && p.home === f.score.home && p.away === f.score.away) {
        return { pts: 3, perfect: true };
    }
    return { pts: 2, perfect: false };
}

function formatKickoff(iso: string): { day: string; date: string; time: string } {
    const d = new Date(iso);
    const days = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
    const months = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
    return {
        day: days[d.getDay()],
        date: `${d.getDate()}. ${months[d.getMonth()]}`,
        time: d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }),
    };
}

function isLocked(f: Fixture): boolean {
    if (['FT', 'AET', 'PEN', '1H', 'HT', '2H', 'ET', 'P', 'LIVE'].includes(f.status)) return true;
    return new Date(f.kickoff).getTime() <= Date.now();
}

/* ----- Mocked leaderboard (no backend persistence yet) ----- */
const MOCK_LEADERBOARD = [
    { name: 'Magnus Olsen',      poeng: 47, perfekte: 4, dept: 'Linje 1' },
    { name: 'Ingrid Berg',       poeng: 44, perfekte: 5, dept: 'Linje 2/3' },
    { name: 'Lars Pedersen',     poeng: 41, perfekte: 3, dept: 'AMU' },
    { name: 'Tom Eriksen',       poeng: 39, perfekte: 2, dept: 'Pakkeri' },
    { name: 'Kari Solberg',      poeng: 36, perfekte: 3, dept: 'HMS' },
    { name: 'Anne Lindgren',     poeng: 34, perfekte: 2, dept: 'Stamina' },
    { name: 'Sigrid Bakke',      poeng: 31, perfekte: 1, dept: 'Salg' },
    { name: 'Per Hansen',        poeng: 28, perfekte: 1, dept: 'Logistikk' },
    { name: 'Marit Strøm',       poeng: 24, perfekte: 0, dept: 'Kvalitet' },
    { name: 'Ola Johansen',      poeng: 22, perfekte: 1, dept: 'Pakkeri' },
];

export default function ProfetenPage() {
    const [sport, setSport] = useState<Sport>('football');
    const [data, setData] = useState<FixtureResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [predictions, setPredictions] = useState<Record<string, Prediction>>(readPredictions);
    const [hiddenBySport, setHiddenBySport] = useState<Record<Sport, Set<string>>>({
        football: new Set(),
        handball: new Set(),
    });
    const hiddenLeagues = hiddenBySport[sport];

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setData(null);
        (async () => {
            try {
                const resp = await fetch(`/intranet/profeten/fixtures?sport=${sport}`, {
                    credentials: 'same-origin',
                });
                if (!resp.ok) {
                    setError(`Kunne ikke hente kamper (${resp.status}).`);
                    return;
                }
                const json = await resp.json();
                if (!cancelled) setData(json);
            } catch (e: any) {
                if (!cancelled) setError(e?.message ?? 'Nettverksfeil.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [sport]);

    function setPrediction(fixtureId: number, patch: Partial<Prediction>) {
        const next = { ...predictions };
        const prev: Prediction = next[fixtureId] ?? { outcome: 'U', home: null, away: null };
        const merged: Prediction = { ...prev, ...patch };
        // Auto-fix outcome when score is fully filled in
        if (patch.home !== undefined || patch.away !== undefined) {
            if (merged.home != null && merged.away != null) {
                if (merged.home > merged.away) merged.outcome = 'H';
                else if (merged.home < merged.away) merged.outcome = 'B';
                else merged.outcome = 'U';
            }
        }
        next[fixtureId] = merged;
        setPredictions(next);
        writePredictions(next);
    }

    const allLeagues = useMemo(() => {
        if (!data) return [] as string[];
        const set = new Set<string>();
        for (const f of [...data.fixtures_upcoming, ...data.fixtures_finished]) set.add(f.league);
        // Stable order: Eliteserien first, then alphabetical
        return Array.from(set).sort((a, b) =>
            a === 'Eliteserien' ? -1 : b === 'Eliteserien' ? 1 : a.localeCompare(b, 'nb'),
        );
    }, [data]);

    const visibleUpcoming = useMemo(
        () => (data ? data.fixtures_upcoming.filter((f) => !hiddenLeagues.has(f.league)) : []),
        [data, hiddenLeagues],
    );
    const visibleFinished = useMemo(
        () => (data ? data.fixtures_finished.filter((f) => !hiddenLeagues.has(f.league)) : []),
        [data, hiddenLeagues],
    );

    function toggleLeague(name: string) {
        setHiddenBySport((prev) => {
            const cur = new Set(prev[sport] ?? []);
            if (cur.has(name)) cur.delete(name);
            else cur.add(name);
            return { ...prev, [sport]: cur };
        });
    }

    const totals = useMemo(() => {
        if (!data) return { pts: 0, perfekte: 0, predicted: 0, scored: 0, total: 0 };
        let pts = 0;
        let perfekte = 0;
        let predicted = 0;
        let scored = 0;
        const allFixtures = [...data.fixtures_upcoming, ...data.fixtures_finished];
        for (const f of allFixtures) {
            const p = predictions[String(f.id)];
            if (p) predicted++;
            const r = pointsFor(f, p);
            if (r) {
                scored++;
                pts += r.pts;
                if (r.perfect) perfekte++;
            }
        }
        return { pts, perfekte, predicted, scored, total: allFixtures.length };
    }, [data, predictions]);

    return (
        <Container maxWidth={false} className={style.page}>
            <motion.header
                className={style.pageHead}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
            >
                <h1 className={style.pageTitle}>
                    <span className={style.pageTitleItalic}>Profeten</span>
                </h1>
            </motion.header>

            <nav className={style.sportTabs} aria-label="Velg idrett">
                {SPORTS.map((s) => (
                    <button
                        key={s.key}
                        type="button"
                        className={`${style.sportTab} ${sport === s.key ? style.sportTabActive : ''}`}
                        onClick={() => setSport(s.key)}
                        aria-pressed={sport === s.key}
                    >
                        {s.label}
                    </button>
                ))}
                {allLeagues.length > 0 && (
                    <div className={style.leaguePills} role="group" aria-label="Filtrer ligaer">
                        {allLeagues.map((name) => {
                            const off = hiddenLeagues.has(name);
                            const lc = name.toLowerCase();
                            const isTournament = lc.startsWith('vm ') || lc.startsWith('em ') || name === 'VM 2026';
                            const isWomen = lc.includes('kvinner');
                            const accent = isWomen ? style.metaPillWomen :
                                           isTournament ? style.metaPillWc : '';
                            return (
                                <button
                                    key={name}
                                    type="button"
                                    onClick={() => toggleLeague(name)}
                                    className={`${style.metaPill} ${off ? style.metaPillOff : ''} ${accent}`}
                                    aria-pressed={!off}
                                >
                                    {name}
                                </button>
                            );
                        })}
                    </div>
                )}
            </nav>

            <div className={style.headRule} />

            {loading && (
                <div className={style.loading}>Henter kamper fra Eliteserien…</div>
            )}

            {error && (
                <div className={style.errorBanner}>
                    <span className={style.errorKicker}>Feil</span>
                    <span>{error}</span>
                </div>
            )}

            {data && (
                <div className={style.layout}>
                    <main className={style.main}>
                        {/* Score panel */}
                        <section className={style.scorePanel}>
                            <div className={style.scoreCol}>
                                <div className={style.scoreKicker}>Dine poeng</div>
                                <div className={style.scoreNumber}>{totals.pts}</div>
                            </div>
                            <div className={style.scoreCol}>
                                <div className={style.scoreKicker}>Perfekte</div>
                                <div className={style.scoreNumberSmall}>{totals.perfekte}</div>
                            </div>
                            <div className={style.scoreCol}>
                                <div className={style.scoreKicker}>Tippet</div>
                                <div className={style.scoreNumberSmall}>
                                    {totals.predicted}<span className={style.scoreSlash}>/</span>{totals.total}
                                </div>
                            </div>
                            <div className={style.scoreCol}>
                                <div className={style.scoreKicker}>Avgjort</div>
                                <div className={style.scoreNumberSmall}>{totals.scored}</div>
                            </div>
                        </section>

                        {hiddenLeagues.size === allLeagues.length && allLeagues.length > 0 && (
                            <div className={style.empty}>
                                <span className={style.emptyKicker}>Ingen ligaer valgt</span>
                                <span className={style.emptyText}>
                                    Velg en liga over for å vise kamper.
                                </span>
                            </div>
                        )}

                        {/* Upcoming fixtures (filtered by selected leagues) */}
                        {visibleUpcoming.length > 0 && (
                            <section className={style.section}>
                                <header className={style.sectionHead}>
                                    <h2 className={style.sectionTitle}>Kommende</h2>
                                    <span className={style.sectionMeta}>
                                        {visibleUpcoming.length} kamper
                                    </span>
                                </header>
                                <div className={style.fixtureList}>
                                    {visibleUpcoming.map((f, i) => (
                                        <FixtureCard
                                            key={f.id}
                                            fixture={f}
                                            prediction={predictions[String(f.id)]}
                                            onSet={(patch) => setPrediction(f.id, patch)}
                                            index={i}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Finished fixtures (filtered by selected leagues) */}
                        {visibleFinished.length > 0 && (
                            <section className={style.section}>
                                <header className={style.sectionHead}>
                                    <h2 className={style.sectionTitle}>Avgjort</h2>
                                    <span className={style.sectionMeta}>
                                        siste {visibleFinished.length}
                                    </span>
                                </header>
                                <div className={style.fixtureList}>
                                    {visibleFinished.map((f, i) => (
                                        <FixtureCard
                                            key={f.id}
                                            fixture={f}
                                            prediction={predictions[String(f.id)]}
                                            onSet={(patch) => setPrediction(f.id, patch)}
                                            index={i}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}
                    </main>

                    {/* Leaderboard sidebar */}
                    <aside className={style.sidebar}>
                        <header className={style.sectionHead}>
                            <h2 className={style.sectionTitle}>Topplista</h2>
                        </header>
                        <ol className={style.leaderboard}>
                            {MOCK_LEADERBOARD.map((p, i) => (
                                <li key={p.name} className={`${style.lbRow} ${i < 3 ? style.lbTop : ''}`}>
                                    <span className={style.lbRank}>{String(i + 1).padStart(2, '0')}</span>
                                    <span className={style.lbName}>
                                        <span>{p.name}</span>
                                        <span className={style.lbDept}>{p.dept}</span>
                                    </span>
                                    <span className={style.lbPts}>
                                        {p.poeng}<span className={style.lbPtsUnit}>&thinsp;p</span>
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </aside>
                </div>
            )}
        </Container>
    );
}

/* ---------------- Fixture card ---------------- */

interface FixtureCardProps {
    fixture: Fixture;
    prediction: Prediction | undefined;
    onSet: (patch: Partial<Prediction>) => void;
    index: number;
}

function FixtureCard({ fixture, prediction, onSet, index }: FixtureCardProps) {
    const ko = formatKickoff(fixture.kickoff);
    const locked = isLocked(fixture);
    const isPlayed = ['FT', 'AET', 'PEN'].includes(fixture.status);
    const result = pointsFor(fixture, prediction);
    const liveStatus = ['1H', 'HT', '2H', 'ET', 'P', 'LIVE'].includes(fixture.status);

    const pickedH = prediction?.outcome === 'H';
    const pickedU = prediction?.outcome === 'U';
    const pickedB = prediction?.outcome === 'B';

    const actual = actualOutcome(fixture);
    const lcLeague = fixture.league.toLowerCase();
    const isWorldCup = fixture.league === 'VM 2026' || lcLeague.startsWith('vm ');
    const isEuro = lcLeague.startsWith('em ');
    const isWomensLeague = lcLeague.includes('kvinner');

    const tournamentChip = isWorldCup || isEuro;
    const leagueClass = isWomensLeague ? style.fxLeagueWomen :
                        tournamentChip ? style.fxLeagueWc : '';

    return (
        <motion.article
            className={`${style.fixture} ${locked ? style.fixtureLocked : ''} ${isPlayed ? style.fixturePlayed : ''} ${tournamentChip ? style.fixtureWorldCup : ''}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.04 + index * 0.04 }}
        >
            <div className={style.fxLeagueRow}>
                <span className={`${style.fxLeague} ${leagueClass}`}>
                    {fixture.league}
                </span>
                {fixture.stage && (
                    <>
                        <span className={style.fxLeagueDot}>·</span>
                        <span className={style.fxStage}>{fixture.stage}</span>
                    </>
                )}
                {liveStatus && <span className={style.fxLive}>● LIVE</span>}
                {isPlayed && !liveStatus && <span className={style.fxFt}>Slutt</span>}
            </div>
            <div className={style.fxHeader}>
                <div className={style.fxKickoff}>
                    <span className={style.fxDay}>{ko.day}</span>
                    <span className={style.fxDate}>{ko.date}</span>
                    <span className={style.fxTime}>{ko.time}</span>
                </div>
                {!isPlayed && !liveStatus && fixture.venue && (
                    <span className={style.fxVenue}>{fixture.venue}</span>
                )}
            </div>

            <div className={style.fxTeams}>
                <div className={`${style.fxTeam} ${style.fxTeamHome} ${actual === 'H' ? style.fxTeamWin : ''}`}>
                    {fixture.home.logo && <img src={fixture.home.logo} alt="" className={style.fxLogo} />}
                    <span className={style.fxTeamName}>{fixture.home.name}</span>
                </div>
                <div className={style.fxScoreOrVs}>
                    {isPlayed ? (
                        <span className={style.fxScore}>
                            {fixture.score.home}<span className={style.fxScoreSep}>–</span>{fixture.score.away}
                        </span>
                    ) : (
                        <span className={style.fxVs}>vs</span>
                    )}
                </div>
                <div className={`${style.fxTeam} ${style.fxTeamAway} ${actual === 'B' ? style.fxTeamWin : ''}`}>
                    <span className={style.fxTeamName}>{fixture.away.name}</span>
                    {fixture.away.logo && <img src={fixture.away.logo} alt="" className={style.fxLogo} />}
                </div>
            </div>

            <div className={style.fxControls}>
                <div className={style.fxOutcome}>
                    <button
                        className={`${style.fxBtn} ${pickedH ? style.fxBtnActive : ''}`}
                        onClick={() => onSet({ outcome: 'H' })}
                        disabled={locked}
                    >
                        H
                    </button>
                    <button
                        className={`${style.fxBtn} ${pickedU ? style.fxBtnActive : ''}`}
                        onClick={() => onSet({ outcome: 'U' })}
                        disabled={locked}
                    >
                        U
                    </button>
                    <button
                        className={`${style.fxBtn} ${pickedB ? style.fxBtnActive : ''}`}
                        onClick={() => onSet({ outcome: 'B' })}
                        disabled={locked}
                    >
                        B
                    </button>
                </div>
                <div className={style.fxScoreInputs}>
                    <input
                        type="number"
                        min={0}
                        max={20}
                        className={style.fxScoreInput}
                        placeholder="–"
                        value={prediction?.home ?? ''}
                        onChange={(e) => {
                            const v = e.target.value;
                            onSet({ home: v === '' ? null : Math.max(0, Math.min(20, Number(v))) });
                        }}
                        disabled={locked}
                        aria-label="Hjemme mål"
                    />
                    <span className={style.fxDash}>–</span>
                    <input
                        type="number"
                        min={0}
                        max={20}
                        className={style.fxScoreInput}
                        placeholder="–"
                        value={prediction?.away ?? ''}
                        onChange={(e) => {
                            const v = e.target.value;
                            onSet({ away: v === '' ? null : Math.max(0, Math.min(20, Number(v))) });
                        }}
                        disabled={locked}
                        aria-label="Borte mål"
                    />
                </div>
            </div>

            {/* Result strip — only when played */}
            {isPlayed && result && (
                <div className={`${style.fxResult} ${result.pts > 0 ? style.fxResultHit : style.fxResultMiss}`}>
                    {prediction ? (
                        <>
                            <span className={style.fxResultKicker}>
                                {result.perfect ? 'Perfekt' : result.pts > 0 ? 'Riktig utfall' : 'Bom'}
                            </span>
                            <span className={style.fxResultText}>
                                Du tippet {prediction.outcome}
                                {prediction.home != null && prediction.away != null
                                    ? ` ${prediction.home}–${prediction.away}`
                                    : ''}
                                {' · '}
                                <strong className={style.fxResultPts}>+{result.pts} p</strong>
                            </span>
                        </>
                    ) : (
                        <>
                            <span className={style.fxResultKicker}>Ikke tippet</span>
                            <span className={style.fxResultText}>0 poeng — frist gikk ut.</span>
                        </>
                    )}
                </div>
            )}

            {locked && !isPlayed && !liveStatus && (
                <div className={style.fxResult}>
                    <span className={style.fxResultKicker}>Frist gikk ut</span>
                    <span className={style.fxResultText}>
                        Tipset er låst. Resultat kommer etter kampslutt.
                    </span>
                </div>
            )}
        </motion.article>
    );
}
